// @ts-strict
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  NgZone,
  Input,
  Output,
  EventEmitter,
  AfterViewInit,
  OnDestroy,
  ViewContainerRef,
} from "@angular/core";
import { 
  LMActionPickerService,
  LMFieldPickerService,
} from "@app/components/modal";
import { SohoAlertDirective, SohoDataGridComponent } from "ids-enterprise-ng";
import { Subject, takeUntil } from "rxjs";
import { LMConfigurationService } from "@app/services";
import { LMFieldPickerSelectOptions } from "@app/components/modal/field-picker/lm-field-picker";

@Component({
  standalone: false,
  selector: "lm-list-personalization-restricted-actions",
  templateUrl: "./lm-list-personalization-restricted-actions.component.html",
  styleUrl: "./lm-list-personalization-restricted-actions.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ LMActionPickerService, LMFieldPickerService ]
})
export class LMListPersonalizationRestrictedActionsComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("actionDataGridComponent", { static: false })
  private dg!: SohoDataGridComponent;
  @ViewChild(SohoAlertDirective) private conditionAlert!: SohoAlertDirective;

  @Input() listUrlInfo!: LMListUrlInfo;
  @Input() businessClass!: string;
  @Input() listName!: string;
  @Input() excludedActions: string[] = []; // array of called out action names
  @Input() restrictedActions: LMReportPersonalizationRestrictedAction[] = [];
  @Input() restrictOpenAction = false;

  @Output() restrictedActionsChange = new EventEmitter<LMReportPersonalizationRestrictedAction[]>();
  @Output() restrictOpenActionChange = new EventEmitter<boolean>();

  private readonly destroyer$ = new Subject<void>();

  public actionGridOptions!: SohoDataGridOptions;
  public selectedAction: LMReportPersonalizationRestrictedAction | null = null;

  private conditionAlertId = 'list-personalization-restricted-actions-conditions-alert';

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly actionPickerService: LMActionPickerService,
    private readonly lmConfigurationService: LMConfigurationService,
    private readonly lmFieldPickerService: LMFieldPickerService
  ) {}

  ngOnInit(): void {
    this.initializeGrid();
  }

  ngAfterViewInit(): void {
    if (this.dg) {
      this.dg.dataset = this.restrictedActions || [];
      if (this.restrictedActions.length > 0) {
        this.setSelectedRowData(0);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  get selectedRow(): SohoDataGridSelectedRow | null {
    return this.dg?.selectedRows()?.[0] || null;
  }

  get selectedRowIndex(): number {
    return this.selectedRow?.idx ?? -1;
  }

  /** Toolbar Events */
  onAddAction(): void {
    this.openActionPickerByType([ "action" ]);
  }

  onRemoveAction(): void {
    const currentIndex = this.selectedRowIndex;
    if (currentIndex === -1) return;

    this.restrictedActions = this.restrictedActions.filter(
      (action, index) => index !== currentIndex
    );
    this.dg.dataset = this.restrictedActions;
    this.selectedAction = null;

    if (this.restrictedActions.length > 0) {
      const newIndex = Math.min(
        currentIndex,
        this.restrictedActions.length - 1
      );
      this.setSelectedRowData(newIndex);
    }
    this.modelChanged();
    this.cdr.detectChanges();
  }

  /** Datagrid Events */
  onRowClicked(event: SohoDataGridRowClicked): void {
    if (!event) return;
    if (event.row > -1) {
      this.setSelectedRowData(event.row);
      // need to revalidate condition field of the selected row?
      this.validateCondition();
      this.conditionAlert.removeAllMessages();
    }
  }

  /** Action Detail Events */
  onDetailChange() {
    if (this.selectedAction) {
      this.updateGrid();
    }
  }

  onGetConditionQualifier = () => {
    const fieldLookUpUrl = this.listUrlInfo?.fieldLookUpUrl || "";

    const options: LMFieldPickerSelectOptions = {
      view: "SimpleConditions",
      businessClass: this.businessClass,
    };
    this.lmFieldPickerService
      .openFieldPickerDialog(options, fieldLookUpUrl, <ViewContainerRef>{})
      .subscribe((result) => {
        if (result?.fieldName && this.selectedAction) {
          this.selectedAction.condition = result.fieldName;
          this.onDetailChange();
        }
      });
  };

  public validateCondition(): void {
    if (this.selectedAction?.condition === "") {
      this.updateConditionAlert();
    } else {
      if (this.selectedAction?.condition)
        this.lmConfigurationService
          .conditionIsValid(
            this.selectedAction.condition,
            this.listUrlInfo.reportPersonalizationUrl || "",
            this.businessClass
          )
          .pipe(takeUntil(this.destroyer$))
          .subscribe((response: LMLplValidationResponse) => {
            this.updateConditionAlert(response);
          });
    }
  }

  /** Private Methods */
  private updateConditionAlert(
    lplValidationResponse?: LMLplValidationResponse
  ): void {
    if (
      !lplValidationResponse ||
      lplValidationResponse.lplStatus === "LPL_VALID"
    ) {
      this.conditionAlert.removeAllMessages();
    } else {
      const validationError = lplValidationResponse.validationError;
      let alertMessage = lplValidationResponse.message || "";

      if (validationError) {
        alertMessage = validationError.errorDescription;
      }
      const alertType =
        validationError && validationError.isWarning ? "info" : "error";
      this.conditionAlert.addInlineMessage(alertMessage, alertType, true, undefined, undefined, this.conditionAlertId);
    }
  }

  private openActionPickerByType(type?: ReadonlyArray<LMActionPickerActionType>): void {
    // exclude actions that are already in the list
    let excludeActions = this.restrictedActions?.map(
      (action) => action.name
    );

    if (Array.isArray(this.excludedActions)) {
      excludeActions = [ ...excludeActions, ...this.excludedActions ];
    }

    this.actionPickerService.openActionPickerDialog(
      this.businessClass,
      this.listName,
      type,
      excludeActions
    )
    .pipe(
      takeUntil(this.destroyer$)
    )
    .subscribe(this.handleAddActions);
  }

  private setSelectedRowData(row: number): void {
    const selectedAction = this.restrictedActions[row];

    if (selectedAction) {
      this.selectedAction = selectedAction;
      this.dg.selectRows(row);
      this.cdr.detectChanges();
    }
  }

  private updateGrid(
    newActions?: LMReportPersonalizationRestrictedAction[]
  ): void {
    this.ngZone.runOutsideAngular(() => {
      if (!this.dg?.dataset) return;

      if (!newActions) {
        const selectedRowIdx = this.selectedRowIndex;

        if (selectedRowIdx === undefined || selectedRowIdx === -1) return;

        this.dg.rowStatus(
          selectedRowIdx,
          "new",
          Soho.Locale.translate("RowDataChanged")
        );
        this.dg.dataset = this.restrictedActions;
        this.setSelectedRowData(selectedRowIdx);
      } else {
        this.restrictedActions = [
          ...this.restrictedActions,
          ...newActions,
        ];

        this.dg.dataset = this.restrictedActions;

        const toolbarActionsLen = this.restrictedActions.length;
        const startIndex = toolbarActionsLen - newActions.length;

        for (let i = startIndex; i < toolbarActionsLen; i++) {
          this.dg.rowStatus(i, "new", Soho.Locale.translate("RowDataAdded"));
        }

        const lastIndex = toolbarActionsLen - 1;

        if (lastIndex >= 0) {
          this.setSelectedRowData(lastIndex);
        }
      }

      this.ngZone.run(() => {
        this.modelChanged();
        this.cdr.detectChanges();
      });
    });
  }

  private modelChanged(): void {
    this.restrictedActionsChange.emit(this.restrictedActions);
  }

  private handleAddActions = (actionSpecs: LMActionSpec[] | null): void => {
    if (!Array.isArray(actionSpecs) || actionSpecs.length === 0) return;

    const newActions: LMReportPersonalizationRestrictedAction[] = actionSpecs.map(
      (action) => ({
        name: action?.name || "",
        label: action.label || "",
        fullName: action.fullName || ""
      })
    );

    this.updateGrid(newActions);
  };

  private initializeGrid(): void {
    this.actionGridOptions = {
      selectable: "single",
      paging: false,
      rowReorder: true,
      useLocaleSort: false,
      editable: false,
      rowHeight: "short",
      isList: true,
      showDirty: true,
      enableTooltips: true,
      headerBackgroundColor: "light",
      disableRowDeactivation: true,
      disableRowDeselection: true,
      columns: [
        {
          id: "name",
          name: Soho.Locale.translate("Name"),
          sortable: true,
          field: "name",
          formatter: Soho.Formatters.Text,
          textOverflow: "ellipsis",
        }
      ],
      dataset: this.restrictedActions,
      emptyMessage: {
        title: Soho.Locale.translate("RestrictedActions_DataGridEmptyTitle"),
        info: Soho.Locale.translate("RestrictedActions_DataGridEmptyMessage"),
      },
    };
  }
}
